import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, CheckCircle2, ShoppingBag, Ticket } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api";
import { SIZE_LABELS, PAYMENT_METHODS } from "@/data";
import { useStore } from "@/context/StoreContext";
import { scrollToId } from "@/components/Hero";

export const CartDrawer = () => {
  const { cart, cartOpen, setCartOpen, setQty, removeItem, clearCart, cartTotal } = useStore();
  const [form, setForm] = useState({ customer_name: "", email: "", phone: "", fulfillment: "pickup", payment_method: "Cash", notes: "", coupon_code: "" });
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const close = () => {
    setCartOpen(false);
    if (done) setTimeout(() => setDone(null), 400);
  };

  const refreshQuote = async (couponOverride) => {
    if (!form.email.includes("@") || !cart.length) {
      setQuote(null);
      return null;
    }
    try {
      const { data } = await api.post("/orders/quote", {
        email: form.email,
        coupon_code: couponOverride !== undefined ? couponOverride : (form.coupon_code || null),
        items: cart.map((i) => ({ flavor_id: i.id, size: i.size, qty: i.qty })),
      });
      setQuote(data);
      return data;
    } catch {
      setQuote(null);
      return null;
    }
  };

  useEffect(() => {
    const t = setTimeout(() => { refreshQuote(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, form.email]);

  const applyCoupon = async () => {
    if (!form.coupon_code.trim()) return;
    if (!form.email.includes("@")) {
      toast.error("Enter your email first — codes are tied to it");
      return;
    }
    setApplying(true);
    const data = await refreshQuote(form.coupon_code.trim());
    setApplying(false);
    if (data?.coupon_error) toast.error(data.coupon_error);
    else if (data?.coupon_discount > 0) toast.success(`Code applied — $${data.coupon_discount.toFixed(2)} off`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!cart.length || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        customer_name: form.customer_name,
        email: form.email,
        phone: form.phone,
        fulfillment: form.fulfillment,
        payment_method: form.payment_method,
        notes: form.notes,
        coupon_code: form.coupon_code || null,
        items: cart.map((i) => ({ flavor_id: i.id, size: i.size, qty: i.qty })),
      });
      setDone(data);
      clearCart();
      setQuote(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const displayTotal = quote ? quote.total : cartTotal;

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
            data-testid="cart-backdrop"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-cosmic-elevated border-l border-white/10 flex flex-col"
            data-testid="cart-drawer">
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
              <h3 className="font-display text-xl font-bold text-white flex items-center gap-2">
                <ShoppingBag size={19} className="text-cosmic-pink" /> Your Order
              </h3>
              <button data-testid="cart-close-button" onClick={close} aria-label="Close cart"
                className="rounded-full glass p-2 text-slate-300 hover:text-white">
                <X size={17} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {done ? (
                <div className="flex h-full flex-col items-center justify-center text-center" data-testid="order-success">
                  <CheckCircle2 size={56} className="text-cosmic-green" />
                  <h4 className="font-display mt-5 text-2xl font-black text-white">Order received!</h4>
                  <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                    Order <span className="font-mono text-cosmic-cyan">#{done.order_id.slice(0, 8).toUpperCase()}</span> is
                    pending confirmation. Total: <span className="font-bold text-white">${done.total.toFixed(2)}</span>
                  </p>
                  {(done.loyalty_discount > 0 || done.coupon_discount > 0) && (
                    <p className="mt-2 text-xs text-cosmic-green">
                      You saved ${(done.loyalty_discount + done.coupon_discount).toFixed(2)} with rewards & codes.
                    </p>
                  )}
                  <p className="mt-2 text-xs text-slate-400">
                    A confirmation email is on its way. We will reach out to arrange pickup.
                  </p>
                  <button data-testid="order-pay-card-button"
                    onClick={async () => {
                      try {
                        const { data } = await api.post("/payments/checkout", { kind: "order", ref_id: done.order_id, origin_url: window.location.origin });
                        window.location.href = data.checkout_url;
                      } catch (err) { toast.error(formatApiError(err)); }
                    }}
                    className="mt-6 w-full rounded-full bg-gradient-to-r from-cosmic-cyan to-cosmic-purple px-7 py-3.5 text-sm font-bold text-white glow-cyan transition-transform duration-300 hover:scale-[1.02]">
                    Pay ${done.total.toFixed(2)} by Card Now
                  </button>
                  <p className="mt-2 text-[11px] text-slate-500">Secure checkout by Stripe — or pay later with your chosen method.</p>
                  <button data-testid="order-success-close" onClick={close}
                    className="mt-7 rounded-full bg-gradient-to-r from-cosmic-pink to-cosmic-purple px-7 py-3 text-sm font-bold text-white">
                    Keep Exploring
                  </button>
                </div>
              ) : cart.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-center" data-testid="cart-empty">
                  <ShoppingBag size={44} className="text-slate-600" />
                  <p className="mt-4 text-sm text-slate-400">Your cart is floating in empty space.</p>
                  <button data-testid="cart-browse-button" onClick={close}
                    className="mt-5 rounded-full glass px-6 py-2.5 text-sm font-bold text-white hover:border-cosmic-cyan/50">
                    Browse Flavors
                  </button>
                </div>
              ) : (
                <>
                  <ul className="space-y-4" data-testid="cart-items">
                    {cart.map((item) => (
                      <li key={item.key} className="glass rounded-2xl p-4" data-testid={`cart-item-${item.key}`}>
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <span className="h-9 w-9 rounded-full border border-white/20" style={{
                              background: `radial-gradient(circle at 35% 30%, ${item.color}, ${item.color}22 75%)`,
                            }} />
                            <div>
                              <div className="text-sm font-bold text-white">{item.name}</div>
                              <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-400">
                                {SIZE_LABELS[item.size]} · ${item.unit}
                              </div>
                            </div>
                          </div>
                          <button data-testid={`cart-remove-${item.key}`} onClick={() => removeItem(item.key)} aria-label="Remove item"
                            className="text-slate-500 hover:text-cosmic-pink transition-colors">
                            <Trash2 size={15} />
                          </button>
                        </div>
                        <div className="mt-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <button data-testid={`cart-minus-${item.key}`} onClick={() => setQty(item.key, item.qty - 1)} aria-label="Decrease"
                              className="rounded-full bg-white/5 p-1.5 text-white hover:bg-white/10"><Minus size={13} /></button>
                            <span className="w-6 text-center text-sm font-bold text-white" data-testid={`cart-qty-${item.key}`}>{item.qty}</span>
                            <button data-testid={`cart-plus-${item.key}`} onClick={() => setQty(item.key, item.qty + 1)} aria-label="Increase"
                              className="rounded-full bg-white/5 p-1.5 text-white hover:bg-white/10"><Plus size={13} /></button>
                          </div>
                          <span className="font-display text-base font-bold text-white">${(item.unit * item.qty).toFixed(2)}</span>
                        </div>
                      </li>
                    ))}
                  </ul>

                  <form onSubmit={submit} className="mt-7 space-y-4" data-testid="order-form">
                    <div className="grid grid-cols-2 gap-3">
                      <input data-testid="order-name-input" required placeholder="Your name" value={form.customer_name} onChange={set("customer_name")}
                        className="col-span-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cosmic-pink/60 focus:outline-none" />
                      <input data-testid="order-email-input" required type="email" placeholder="Email" value={form.email} onChange={set("email")}
                        className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cosmic-pink/60 focus:outline-none" />
                      <input data-testid="order-phone-input" required type="tel" placeholder="Phone" value={form.phone} onChange={set("phone")}
                        className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cosmic-pink/60 focus:outline-none" />
                    </div>

                    <div>
                      <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">Fulfillment</span>
                      <div className="flex gap-2">
                        {[["pickup", "Pickup"], ["delivery", "Local Delivery"]].map(([val, label]) => (
                          <button type="button" key={val} data-testid={`order-fulfillment-${val}`}
                            onClick={() => setForm((f) => ({ ...f, fulfillment: val }))}
                            className={`flex-1 rounded-full py-2.5 text-xs font-bold transition-colors ${form.fulfillment === val ? "bg-white text-cosmic-base" : "bg-white/5 text-slate-300"}`}>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">Pay with</span>
                      <div className="flex flex-wrap gap-2">
                        {PAYMENT_METHODS.map((pm) => (
                          <button type="button" key={pm.name} data-testid={`order-pay-${pm.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
                            onClick={() => setForm((f) => ({ ...f, payment_method: pm.name }))}
                            className={`rounded-full px-3.5 py-2 text-xs font-bold transition-colors ${form.payment_method === pm.name ? "bg-gradient-to-r from-cosmic-pink to-cosmic-purple text-white" : "bg-white/5 text-slate-300"}`}>
                            {pm.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <span className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
                        <Ticket size={11} /> Have a code?
                      </span>
                      <div className="flex gap-2">
                        <input data-testid="order-coupon-input" placeholder="Coupon code" value={form.coupon_code} onChange={set("coupon_code")}
                          className="flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 font-mono text-sm uppercase tracking-wider text-white placeholder:text-slate-500 placeholder:normal-case placeholder:tracking-normal focus:border-cosmic-yellow/60 focus:outline-none" />
                        <button type="button" data-testid="order-coupon-apply" onClick={applyCoupon} disabled={applying}
                          className="rounded-xl bg-cosmic-yellow/15 border border-cosmic-yellow/40 px-4 text-xs font-bold text-cosmic-yellow hover:bg-cosmic-yellow/25 disabled:opacity-50">
                          {applying ? "…" : "Apply"}
                        </button>
                      </div>
                    </div>

                    <textarea data-testid="order-notes-input" placeholder="Notes (pickup time, event, allergies…)" value={form.notes} onChange={set("notes")} rows={2}
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cosmic-pink/60 focus:outline-none" />

                    <div className="space-y-1.5 border-t border-white/10 pt-4 text-sm">
                      <div className="flex justify-between text-slate-400">
                        <span>Subtotal</span>
                        <span data-testid="order-subtotal">${(quote ? quote.subtotal : cartTotal).toFixed(2)}</span>
                      </div>
                      {quote?.loyalty_discount > 0 && (
                        <div className="flex justify-between text-cosmic-green" data-testid="order-loyalty-discount">
                          <span>Cosmic Club reward{quote.free_units ? ` (${quote.free_units} free)` : ""}{quote.half_units ? ` (${quote.half_units} half off)` : ""}</span>
                          <span>−${quote.loyalty_discount.toFixed(2)}</span>
                        </div>
                      )}
                      {quote?.coupon_discount > 0 && (
                        <div className="flex justify-between text-cosmic-yellow" data-testid="order-coupon-discount">
                          <span>Coupon {quote.coupon_code}</span>
                          <span>−${quote.coupon_discount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between pt-1">
                        <span className="text-slate-300">Total</span>
                        <span data-testid="cart-total" className="font-display text-2xl font-black text-gradient-candy">${displayTotal.toFixed(2)}</span>
                      </div>
                      {quote && !quote.is_member && (
                        <button type="button" data-testid="order-join-club-hint"
                          onClick={() => { close(); setTimeout(() => scrollToId("#loyalty"), 350); }}
                          className="w-full pt-1 text-left text-[11px] text-cosmic-cyan hover:underline">
                          This email isn't in the Cosmic Club yet — join free and start earning free cotton candy →
                        </button>
                      )}
                    </div>

                    <button data-testid="order-submit-button" type="submit" disabled={submitting}
                      className="w-full rounded-full bg-gradient-to-r from-cosmic-pink to-cosmic-purple py-4 font-display text-sm font-bold text-white transition-transform duration-300 hover:scale-[1.02] glow-pink disabled:opacity-50 disabled:hover:scale-100">
                      {submitting ? "Sending…" : "Submit Order Request"}
                    </button>
                    <p className="text-center text-[11px] text-slate-500">No online charge — we confirm first, then you pay your way.</p>
                  </form>
                </>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
